import React, { useState } from "react";
import { createPortal } from "react-dom";
import fetchWithCredentials from "apiClient";
import { Button } from "components/ui/Button";
import { LegalDocumentContent } from "components/LegalDocumentContent";
import { LegalDocumentPayload } from "types/Legal";
import { useAuth } from "context/AuthContext";

interface TermsModalProps {
  document: LegalDocumentPayload;
  onAccepted: () => void;
}

export function TermsModal({ document: doc, onAccepted }: TermsModalProps) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  const handleAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    setError(null);
    try {
      await fetchWithCredentials("/api/legal/accept/", {
        method: "POST",
        body: JSON.stringify({ legal_document_id: doc.id }),
      });
      onAccepted();
    } catch (err) {
      // Backend returns 200 with already_accepted when already accepted; any throw is a real error
      setError(
        locale === "es"
          ? "No se pudo registrar la aceptación. Intente de nuevo."
          : "Could not record your acceptance. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-60"
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-title"
        >
          <h2
            id="terms-title"
            className="text-xl font-semibold text-gray-900 p-4 border-b border-gray-200 shrink-0"
          >
            {locale === "es" ? "Términos y Condiciones" : "Terms and Conditions"}
          </h2>
          <div className="overflow-y-auto p-4 flex-1 min-h-0 border-b border-gray-200">
            <LegalDocumentContent
              content={doc.content || (locale === "es" ? "Sin contenido." : "No content.")}
            />
          </div>
          <div className="p-4 space-y-3 shrink-0">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">
                {locale === "es"
                  ? "He leído y acepto los Términos y Condiciones"
                  : "I have read and accept the Terms and Conditions"}
              </span>
            </label>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <Button
              onClick={handleAccept}
              disabled={!checked || submitting}
              variant="primary"
              size="sm"
            >
              {submitting
                ? locale === "es"
                  ? "Enviando..."
                  : "Sending..."
                : locale === "es"
                  ? "Aceptar"
                  : "Accept"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
